import React from 'react';
import { Card, Button, Badge } from '../ui';
import SportIcon from '../common/SportIcon';
import { getSportLabel } from '../../constants/sportTheme';
import { cn } from '../../design-system/cn';
import { motion, shadows } from '../../design-system/tokens';
import {
  formatTimeRemaining,
  getOpenLockDisplayStatus,
  resolveOpenLockGameLabel,
} from '../../api/openChallengeService';

function formatStartLabel(startTime) {
  if (!startTime) return null;
  const t = new Date(startTime).getTime();
  if (Number.isNaN(t)) return null;
  const diff = t - Date.now();
  if (diff <= 0) return 'Started';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Starts in ${mins}m`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `Starts in ${hours}h ${rem}m` : `Starts in ${hours}h`;
}

export default function OpenArenaLockCard({
  challenge,
  currentUserId,
  gameById = {},
  onJoin,
  onCancel,
  joinBusy,
  cancelBusy,
  joinDisabled = false,
}) {
  const isMine = challenge.creatorId === currentUserId;
  const busy = joinBusy || cancelBusy;
  const status = getOpenLockDisplayStatus(challenge);
  const gameInfo = resolveOpenLockGameLabel(challenge, gameById);
  const timeLeft = formatTimeRemaining(challenge.expiresAt);
  const startLabel = formatStartLabel(gameInfo?.startTime);
  const joinable = !isMine
    && challenge.status === 'open'
    && status.label !== 'Expired'
    && !joinDisabled;

  return (
    <Card
      variant="interactive"
      padding="md"
      rounded="2xl"
      className={cn(motion.transition, shadows.elevation.interactive)}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          {gameInfo?.sport && (
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-400/90 mb-1 inline-flex items-center gap-1.5">
              <SportIcon sport={gameInfo.sport} size="xs" decorative />
              <span>{getSportLabel(gameInfo.sport)}</span>
            </p>
          )}
          {gameInfo?.matchup ? (
            <p className="text-sm font-black text-white leading-snug">{gameInfo.matchup}</p>
          ) : (
            <p className="text-sm font-black text-white leading-snug">{challenge.wager}</p>
          )}
        </div>
        <Badge variant={status.tone}>{status.label}</Badge>
      </div>

      {gameInfo?.matchup && challenge.wager && (
        <p className="text-xs text-gray-400 font-medium mb-2 line-clamp-2">{challenge.wager}</p>
      )}

      <div className="space-y-1 text-[10px] font-bold uppercase tracking-widest text-gray-500">
        <p>
          Entry:{' '}
          <span className="text-emerald-300 tabular-nums">${Number(challenge.stake).toFixed(0)}</span>
        </p>
        {challenge.creatorSide && (
          <p>
            Pick: <span className="text-gray-300 normal-case">{challenge.creatorSide}</span>
          </p>
        )}
        <p>
          Created by:{' '}
          <span className="text-gray-300 normal-case">{challenge.creatorUsername || 'Player'}</span>
        </p>
        {startLabel && (
          <p>
            <span className="text-gray-400 normal-case">{startLabel}</span>
          </p>
        )}
        {timeLeft && status.label !== 'Expired' && (
          <p>
            Accept within: <span className="text-amber-300/90 normal-case">{timeLeft}</span>
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {isMine ? (
          challenge.status === 'open' && status.label !== 'Expired' ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => onCancel?.(challenge.id)}
              className="min-h-0 text-[9px] uppercase tracking-widest"
            >
              {cancelBusy ? 'Cancelling…' : 'Cancel'}
            </Button>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">
              {status.label === 'Expired' ? 'Expired' : 'Waiting for opponent'}
            </span>
          )
        ) : joinable ? (
          <Button
            type="button"
            size="sm"
            disabled={busy || !currentUserId}
            onClick={() => onJoin?.(challenge)}
            className="min-h-0 text-[9px] uppercase tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
          >
            {joinBusy ? 'Locking entry…' : 'Join'}
          </Button>
        ) : (
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-600">
            {status.label === 'Expired' ? 'Expired' : 'Unavailable'}
          </span>
        )}
      </div>
    </Card>
  );
}
